import Link from "next/link";
import type {
  StoryblokRichtext,
  StoryblokRichtextContent,
} from "storyblok-rich-text-react-renderer";
import {
  MARK_LINK,
  NODE_PARAGRAPH,
  render,
} from "storyblok-rich-text-react-renderer";

import type { AnnouncementTextColor } from "@/bloks/SiteSettings/SiteSettings";

import styles from "./announcementBar.module.scss";

/* Storyblok returns an empty paragraph rather than null for a cleared
   richtext, so we have to look for actual content before rendering. */
const hasContent = (node?: StoryblokRichtextContent): boolean => {
  if (!node) return false;
  if (node.type === "text") return Boolean(node.text?.trim());
  if (node.type === "blok" || node.type === "image" || node.type === "emoji")
    return true;
  return node.content?.some(hasContent) ?? false;
};

interface UIProps {
  announcement?: StoryblokRichtext;
  backgroundColor?: string;
  textColor?: AnnouncementTextColor;
}

const UI = ({ announcement, backgroundColor, textColor }: UIProps) => {
  if (!announcement?.content?.some(hasContent)) return null;

  return (
    <div
      className={`${styles.container} ${
        textColor === "dark" ? styles.dark : styles.light
      }`}
      style={{ backgroundColor: backgroundColor || undefined }}
    >
      <div className={styles.content}>
        {render(announcement, {
          markResolvers: {
            [MARK_LINK]: (children, props) => {
              const { href, target, linktype } = props;

              if (href?.match(/^(https?:)?\/\//)) {
                return (
                  <a href={href} target={target}>
                    {children}
                  </a>
                );
              }

              if (href?.match(/^mailto:/) || linktype === "email") {
                return (
                  <a href={`mailto:${href}`} target={target}>
                    {children}
                  </a>
                );
              }

              return <Link href={href || ""}>{children}</Link>;
            },
          },
          nodeResolvers: {
            [NODE_PARAGRAPH]: (children) => (
              <p className={styles.text}>{children}</p>
            ),
          },
        })}
      </div>
    </div>
  );
};

export default UI;
